import { useState, useEffect } from 'react'
import { useMutation, useQuery } from '@apollo/client/react'

import { GET_CUSTOMER_FAVOURITES, UPDATE_FAVOURITE } from '@graphql'
import { getToken, getUserData } from '@utils'
import { MutationResponse } from '@types'

export { useRelatedBreeds } from './useRelatedBreeds'

export const useFavourites = () => {
  const [userData, setUserData] = useState(getUserData(getToken()))
  const [favourites, setFavourites] = useState([])
  const [selectedDog, setSelectedDog] = useState(null)

  const { loading: _favouriteLoading, data: _favouriteData } = useQuery(GET_CUSTOMER_FAVOURITES, {
    skip: !userData,
    fetchPolicy: 'cache-and-network', // Ensures fresh data on mount
  })

  let favouriteLoading
  let favouriteData
  if (userData) {
    favouriteLoading = _favouriteLoading
    favouriteData = _favouriteData
  }

  useEffect(() => {
    if (favouriteData?.customer?.favourites) {
      setFavourites(favouriteData.customer.favourites)
    }
  }, [favouriteData])

  const [updateFavourite] = useMutation<MutationResponse<'updateFavourite'>>(UPDATE_FAVOURITE, {
    refetchQueries: [{ query: GET_CUSTOMER_FAVOURITES }],
    awaitRefetchQueries: true, // Wait for refetch before completing
    onCompleted: (response) => {
      if (response.updateFavourite) {
        setFavourites((cur) => {
          const newFavourites = [...cur]
          const res = newFavourites.filter(
            (favourite) => favourite.id !== response.updateFavourite.id
          )
          if (res.length === newFavourites.length) {
            res.push(response.updateFavourite)
          }
          return res
        })
      }
    },
    onError: (error) => {
      console.error('updateFavourite error:', error)
    },
  })

  const toggleFavourite = (dogId: string | number) => {
    if (userData) {
      updateFavourite({
        variables: { dog: dogId },
      })
      return true
    } else {
      setSelectedDog(dogId)
      return false
    }
  }

  const isFavourite = (dogId: string | number) => {
    return favourites.some((fav) => fav.id === dogId)
  }

  const refreshUserData = () => {
    setUserData(getUserData(getToken()))
  }

  return {
    favourites,
    userData,
    selectedDog,
    setSelectedDog,
    toggleFavourite,
    isFavourite,
    updateFavourite,
    refreshUserData,
    favouriteLoading,
  }
}
